/**
 * QA Feedback Plan API
 *
 * Wraps `POST /reports/:id/feedback/plan`. The endpoint reads the
 * persisted technical evaluation, open comments, and revision requests
 * for a report and returns a bucketed regeneration plan the FE shows in
 * a preview modal before any LLM work runs. PR-1 is read-only -- the
 * "Apply" pathway is wired in PR-2 (`applyFeedbackPlan`).
 */

import { getAPIClient } from './client';

/** A single sanitized feedback string with provenance for audit. */
export interface FeedbackPlanItem {
  text: string;
  source_id?: string | null;
}

export interface FeedbackBucketV2 {
  criterion_ids: string[];
  staff_position_ids: string[];
  capability_ids: string[];
  /** keyed `${kind}:${target_id}` -> ordered feedback items */
  feedback_by_target: Record<string, FeedbackPlanItem[]>;
  /** Reviewer's free-form extra instructions / spillover items. */
  global_instructions?: string;
}

export interface StaffResearchTarget {
  position_id: string;
  role_title?: string;
  queries?: string[];
  reason?: string;
}

export interface FeedbackBucketSectionRegen {
  section_ids: string[];
  included_artifact_ids: string[];
  excluded_artifact_ids: string[];
  search_queries?: string[];
  bucket_ids?: string[];
  staff_position_ids?: string[];
  staff_role_titles?: Record<string, string>;
  staff_research?: StaffResearchTarget[];
}

export interface FeedbackBucketFullRegen {
  trigger_reasons: string[];
  estimated_runtime_minutes: number;
  /** 'targeted' when only a small number of criteria need V1 research; 'full' otherwise. */
  regen_mode?: 'targeted' | 'full';
  /** Criterion IDs to re-research in targeted mode. */
  targeted_criterion_ids?: string[];
}

export interface FeedbackManualOnlyItem {
  comment_id: string;
  reason: string;
}

export type FeedbackPlanRecommendation =
  | 'v2_prompt_augmented'
  | 'section_regen'
  | 'targeted_section_regen'
  | 'v1v2_full_regen'
  | 'manual_only'
  | 'none';

export interface FeedbackPlanSummary {
  v2_target_count: number;
  section_regen_count: number;
  staff_research_count?: number;
  staff_research_triggered?: boolean;
  full_regen_triggered: boolean;
  /** True when only 1-3 missing criteria trigger a fast targeted V1+V2 regen (~2 min). */
  targeted_regen_triggered?: boolean;
  targeted_criterion_count?: number;
  manual_count: number;
  grammar_findings_count?: number;
  consumed_comment_ids: string[];
  consumed_revision_request_ids: string[];
  min_severity: 'minor' | 'major' | 'critical';
}

export interface FeedbackPlanPayload {
  report_id: string;
  buckets: {
    v2_prompt_augmented: FeedbackBucketV2;
    section_regen: FeedbackBucketSectionRegen;
    v1v2_full_regen: FeedbackBucketFullRegen;
    manual_only: FeedbackManualOnlyItem[];
  };
  summary: FeedbackPlanSummary;
  recommended_action: FeedbackPlanRecommendation;
}

export interface FeedbackPlanResponse {
  plan_id: string;
  report_id: string;
  payload: FeedbackPlanPayload;
  created_at: string;
  expires_at?: string | null;
  recommended_action: FeedbackPlanRecommendation;
}

/** Bucket A has per-target bullets and/or global guidance to thread into V2. */
export function feedbackPlanHasV2PromptWork(payload: FeedbackPlanPayload): boolean {
  const v2 = payload.buckets?.v2_prompt_augmented;
  if (!v2) return false;
  if (Object.keys(v2.feedback_by_target ?? {}).length > 0) return true;
  return Boolean(v2.global_instructions?.trim());
}

/**
 * Action the Apply button should use. Older cached plans may say `none` while
 * still carrying global_instructions — treat those as V2 prompt regen.
 */
export function effectiveFeedbackPlanRecommendation(
  plan: FeedbackPlanResponse,
): FeedbackPlanRecommendation {
  if (
    plan.recommended_action === 'none' &&
    feedbackPlanHasV2PromptWork(plan.payload)
  ) {
    return 'v2_prompt_augmented';
  }
  return plan.recommended_action;
}

export interface FeedbackPlanRequest {
  evaluation_key?: string;
  include_comments?: boolean;
  include_revision_requests?: boolean;
  include_grammar_findings?: boolean;
  extra_instructions?: string;
  min_severity?: 'minor' | 'major' | 'critical';
  force_refresh?: boolean;
}

/**
 * Human-readable summary of reviewer bullets that V2 regeneration threads into
 * per-target LLM prompts (Bucket A). Useful for reviewer “approve after preview”.
 */
export function buildFeedbackPlanPromptPreviewText(payload: FeedbackPlanPayload): string {
  const v2 = payload.buckets?.v2_prompt_augmented;
  if (!v2) return 'No Bucket A payload in plan.';

  const lines: string[] = [];
  lines.push(
    'Sanitized reviewer guidance threaded into criterion / staff / company prompts:',
  );
  lines.push('');

  if (v2.global_instructions?.trim()) {
    lines.push('Global guidance');
    lines.push(v2.global_instructions.trim());
    lines.push('');
  }

  const entries = Object.entries(v2.feedback_by_target ?? {}).sort(
    ([, aItems], [, bItems]) => {
      const aCorr = (aItems ?? []).some((it) =>
        String(it.source_id ?? '').startsWith('correction:'),
      );
      const bCorr = (bItems ?? []).some((it) =>
        String(it.source_id ?? '').startsWith('correction:'),
      );
      if (aCorr && !bCorr) return -1;
      if (!aCorr && bCorr) return 1;
      return 0;
    },
  );
  if (entries.length === 0) {
    lines.push(
      '(No per-target reviewer bullets — only global guidance above applies, if any.)',
    );
  } else {
    for (const [targetKey, items] of entries) {
      lines.push(`[ ${targetKey} ]`);
      for (const it of items ?? []) lines.push(`  • ${it.text}`);
      lines.push('');
    }
  }

  lines.push('—');
  lines.push('Staff re-research targets (Bucket B1):');
  const staffResearch = payload.buckets?.section_regen?.staff_research ?? [];
  if (staffResearch.length === 0) {
    lines.push('  (none)');
  } else {
    for (const target of staffResearch) {
      const role = target.role_title || target.position_id;
      lines.push(`  • ${role} (${target.position_id})`);
      if (target.reason?.trim()) lines.push(`    Why: ${target.reason.trim()}`);
      for (const q of target.queries ?? []) {
        if (q?.trim()) lines.push(`    Search: ${q.trim()}`);
      }
    }
  }

  lines.push('—');
  lines.push('Routing hints (may require section RAG re-run or full regen separately):');
  const secIds = payload.buckets?.section_regen?.section_ids ?? [];
  if (secIds.length) lines.push(`Section RAG: ${secIds.join(', ')}`);
  else lines.push('Section RAG: none');
  const searchQueries = payload.buckets?.section_regen?.search_queries ?? [];
  if (searchQueries.length) {
    lines.push(`Search queries: ${searchQueries.join(' | ')}`);
  }
  if (payload.summary?.full_regen_triggered) {
    lines.push('Full V1+V2 regeneration recommended — use Report jobs queue if applicable.');
  } else {
    lines.push('Full V1+V2 regeneration: not flagged by planner.');
  }
  lines.push(`Manual-only items queued: ${payload.summary?.manual_count ?? 0}`);

  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// V2 diff endpoint
// ---------------------------------------------------------------------------

export interface V2DiffResponse {
  report_id: string;
  has_previous: boolean;
  previous?: string | null;
  current?: string | null;
}

/**
 * Fetch the before/after V2 snapshot stored when the last regeneration job ran.
 * Returns `has_previous: false` when no snapshot exists (e.g. first-ever regen).
 */
export async function getV2Diff(reportId: string): Promise<V2DiffResponse> {
  const client = getAPIClient();
  return client.get<V2DiffResponse>(`/reports/${reportId}/diff`);
}

/**
 * Compute (and persist) a bucketed feedback plan for a report.
 *
 * The server auto-refreshes stale evaluations (metadata-only bumps or inline
 * re-score) before building the plan — callers should not need to handle 409.
 */
export async function createFeedbackPlan(
  reportId: string,
  body?: FeedbackPlanRequest,
): Promise<FeedbackPlanResponse> {
  const client = getAPIClient();
  return client.post<FeedbackPlanResponse>(
    `/reports/${reportId}/feedback/plan`,
    body ?? {},
  );
}

/* -------------------------------------------------------------------------
 * Apply path (PR-2)
 *
 * The "Apply" button on the FeedbackPlanPreviewModal calls
 * `applyFeedbackPlan`, which routes through the existing
 * `PATCH /reports/{id}/regenerate` endpoint. The endpoint accepts either
 * a stored `feedback_plan_id` (preferred) or an inline payload. PR-2
 * surfaces the plan-id path; the inline path stays available for callers
 * that bypass the modal (e.g. the future review-submission panel).
 * ------------------------------------------------------------------------- */

/** Inline feedback shape that mirrors the backend `InlineFeedbackPayload`. */
export interface InlineFeedbackPayload {
  criterion_feedback?: Record<string, string[]>;
  staff_feedback?: Record<string, string[]>;
  capability_feedback?: Record<string, string[]>;
  global_instructions?: string;
}

export interface ApplyFeedbackPlanRequest {
  feedback_plan_id?: string;
  feedback?: InlineFeedbackPayload;
  /** Reserved for surgical multi-section selection; not enforced server-side today. */
  section_indices?: number[];
}

/**
 * Minimal shape of the queued regeneration job returned by the backend.
 *
 * Defined locally to avoid a circular import on the larger ReportJobStatus
 * type in `reports.ts`. Callers that need the full job status should poll
 * `getReportJobStatus` from `reports.ts` using the `id` returned here.
 */
export interface RegenerationJobStatus {
  id: string;
  project_id?: string;
  type: string;
  status: string;
  input_data?: Record<string, unknown>;
  output_data?: Record<string, unknown>;
  progress_data?: Record<string, unknown>;
  error_message?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  created_at?: string | null;
}

/**
 * Discriminated error types the apply path can raise. Code is the backend
 * `detail.code` so callers can render a stable, translatable message.
 */
export type FeedbackPlanApplyErrorCode =
  | 'feedback_plan_not_found'
  | 'feedback_plan_already_applied'
  | 'evaluation_stale_or_missing'
  | 'evaluation_rescore_failed';

export class FeedbackPlanApplyError extends Error {
  public readonly status: number;
  public readonly code: FeedbackPlanApplyErrorCode | string;
  public readonly detail: unknown;
  /** Backend-provided URL to re-score the proposal (present on evaluation_stale_or_missing). */
  public readonly rescoreUrl?: string;

  constructor(status: number, code: string, message: string, detail?: unknown) {
    super(message);
    this.name = 'FeedbackPlanApplyError';
    this.status = status;
    this.code = code;
    this.detail = detail;
    const d = detail as Record<string, unknown> | undefined;
    this.rescoreUrl = typeof d?.rescore_url === 'string' ? d.rescore_url : undefined;
  }
}

/**
 * Apply a feedback plan by kicking off a V2 regeneration job that will
 * thread the plan's per-target reviewer instructions into the structured
 * generator prompts. The returned job id should be polled via
 * `getReportJobStatus` to drive the existing V2 progress UI.
 */
export async function applyFeedbackPlan(
  reportId: string,
  request: ApplyFeedbackPlanRequest,
): Promise<RegenerationJobStatus> {
  if (!request.feedback_plan_id && !request.feedback) {
    throw new FeedbackPlanApplyError(
      400,
      'invalid_request',
      'applyFeedbackPlan requires either feedback_plan_id or inline feedback.',
    );
  }
  const client = getAPIClient();
  try {
    return await client.patch<RegenerationJobStatus>(
      `/reports/${reportId}/regenerate`,
      request,
    );
  } catch (err) {
    const status = (err as { status?: number })?.status ?? 0;
    const detail = (err as { body?: { detail?: { code?: string; message?: string } } })
      ?.body?.detail;
    const code = detail?.code ?? (status === 404 ? 'feedback_plan_not_found' : 'unknown');
    const message =
      detail?.message ?? (err instanceof Error ? err.message : 'Failed to apply feedback plan.');
    throw new FeedbackPlanApplyError(status, code, message, detail);
  }
}

export interface StaffCorrectionItem {
  position_id: string;
  role_title?: string;
  action?: string;
  reject_staff_member_id?: string;
  assign_staff_member_id?: string;
  reason?: string;
  search_hints?: string[];
}

export interface CompanyCorrectionItem {
  bucket_id: string;
  action?: string;
  reject_project_labels?: string[];
  pin_entity_ids?: string[];
  pin_artifact_ids?: string[];
  search_hints?: string[];
  reason?: string;
}

export interface SelectionCorrectionsPayload {
  staff: StaffCorrectionItem[];
  company: CompanyCorrectionItem[];
}

export interface SelectionCorrectionsState extends SelectionCorrectionsPayload {
  version?: number;
  updated_at?: string | null;
  updated_by?: string | null;
  rejected_staff_member_ids?: string[];
  history?: Array<Record<string, unknown>>;
}

export async function getSelectionCorrections(
  reportId: string,
): Promise<{ selection_corrections: SelectionCorrectionsState }> {
  const client = getAPIClient();
  return client.get(`/reports/${reportId}/selection-corrections`);
}

export async function putSelectionCorrections(
  reportId: string,
  payload: SelectionCorrectionsPayload,
): Promise<{ selection_corrections: SelectionCorrectionsState; staff_assignments_synced?: boolean }> {
  const client = getAPIClient();
  return client.put(`/reports/${reportId}/selection-corrections`, payload);
}
